import CelebrationSharpIcon from "@mui/icons-material/CelebrationSharp";
import FilterVintageSharpIcon from "@mui/icons-material/FilterVintageSharp";
import FlareSharpIcon from "@mui/icons-material/FlareSharp";
import AcUnitSharpIcon from "@mui/icons-material/AcUnitSharp";
import StarPurple500SharpIcon from "@mui/icons-material/StarPurple500Sharp";
import MotionPhotosOffSharpIcon from "@mui/icons-material/MotionPhotosOffSharp";
import { PaletteColorHex } from "../types/PaletteColor.ts";

export type EffectName =
  | "Cracker"
  | "Flower"
  | "Fireworks"
  | "Snow"
  | "Star"
  | "None";

interface Effect {
  name: EffectName;
  icon: React.ElementType;
  logic: (x: number, y: number) => void;
}

const createBurst = (
  x: number,
  y: number,
  count: number,
  properties: {
    colors: string[];
    shapes: ("circle" | "square" | "triangle" | "petal" | "star" | "line")[];
    sizeRange?: { min: number; max: number };
  }
) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "0";
  container.style.width = "100%";
  container.style.height = "100%";
  container.style.pointerEvents = "none";
  container.style.zIndex = "9999";
  document.body.appendChild(container);

  for (let i = 0; i < count; i++) {
    const particle = document.createElement("div");
    particle.style.position = "absolute";
    particle.style.left = `${x}px`;
    particle.style.top = `${y}px`;

    const color =
      properties.colors[Math.floor(Math.random() * properties.colors.length)];
    const shape =
      properties.shapes[Math.floor(Math.random() * properties.shapes.length)];
    const sizeRange = properties.sizeRange ?? { min: 6, max: 14 };
    const size =
      Math.random() * (sizeRange.max - sizeRange.min) + sizeRange.min;

    switch (shape) {
      case "square":
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.backgroundColor = color;
        break;
      case "triangle":
        particle.style.width = "0";
        particle.style.height = "0";
        particle.style.borderLeft = `${size / 2}px solid transparent`;
        particle.style.borderRight = `${size / 2}px solid transparent`;
        particle.style.borderBottom = `${size}px solid ${color}`;
        break;
      case "petal":
        particle.style.width = `${size}px`;
        particle.style.height = `${size / 2}px`;
        particle.style.backgroundColor = color;
        particle.style.borderRadius = "50% 0";
        break;
      case "star":
        particle.style.clipPath =
          "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)";
        particle.style.width = `${size * 1.5}px`;
        particle.style.height = `${size * 1.5}px`;
        particle.style.backgroundColor = color;
        break;
      case "line": // ★ はなび用の「線」形状を追加
        particle.style.width = `${size * 2}px`;
        particle.style.height = `2px`;
        particle.style.backgroundColor = color;
        particle.style.borderRadius = "1px";
        break;
      default: // circle
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.backgroundColor = color;
        particle.style.borderRadius = "50%";
    }

    const angle = Math.random() * 360;
    const distance = Math.random() * 100 + 50;
    const translateX = Math.cos(angle * (Math.PI / 180)) * distance;
    const translateY = Math.sin(angle * (Math.PI / 180)) * distance;
    const rotation = Math.random() * 720 - 360;

    particle.style.transition =
      "transform 0.8s ease-out, opacity 0.8s ease-out";
    particle.style.transform = "translate(-50%, -50%)";
    particle.style.opacity = "1";

    requestAnimationFrame(() => {
      particle.style.transform = `translate(calc(-50% + ${translateX}px), calc(-50% + ${translateY}px)) rotate(${rotation}deg) scale(0)`;
      particle.style.opacity = "0";
    });

    container.appendChild(particle);
  }

  setTimeout(() => {
    document.body.removeChild(container);
  }, 800);
};

const createFlowerSpiral = (
  x: number,
  y: number,
  count: number,
  properties: { colors: string[] }
) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "0";
  container.style.width = "100%";
  container.style.height = "100%";
  container.style.pointerEvents = "none";
  container.style.zIndex = "9999";
  document.body.appendChild(container);

  // 黄金角で配置すると花びらが自然な螺旋状に並ぶ
  const GOLDEN_ANGLE = 137.5;
  const SWIRL_DEGREES = 220; // 広がりながら回転する角度
  const DURATION = 1100;
  const STAGGER = 14; // 中心から順に開いていくよう1枚ずつ遅らせる

  for (let i = 0; i < count; i++) {
    const particle = document.createElement("div");
    particle.style.position = "absolute";
    particle.style.left = `${x}px`;
    particle.style.top = `${y}px`;

    const color =
      properties.colors[Math.floor(Math.random() * properties.colors.length)];
    const size = Math.random() * 8 + 6;
    particle.style.width = `${size}px`;
    particle.style.height = `${size / 2}px`;
    particle.style.backgroundColor = color;
    particle.style.borderRadius = "50% 0";
    particle.style.opacity = "0";

    const baseAngle = i * GOLDEN_ANGLE;
    const maxDistance = 50 + (i / count) * 70 + Math.random() * 20;

    // 螺旋軌道をキーフレームでサンプリングして曲線的に飛ばす
    const steps = 6;
    const keyframes: Keyframe[] = [];
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const angle = baseAngle + SWIRL_DEGREES * t;
      const rad = angle * (Math.PI / 180);
      const dist = maxDistance * t;
      const tx = Math.cos(rad) * dist;
      const ty = Math.sin(rad) * dist;
      // 花びらのとがった軸（borderRadius 50% 0 の対角線）を進行方向へ向ける
      const petalRotation = angle + 45;
      const scale = 0.4 + 0.6 * Math.min(t / 0.3, 1);
      const opacity = t < 0.15 ? t / 0.15 : t > 0.7 ? 1 - (t - 0.7) / 0.3 : 1;
      keyframes.push({
        transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) rotate(${petalRotation}deg) scale(${scale})`,
        opacity,
      });
    }

    particle.animate(keyframes, {
      duration: DURATION,
      delay: i * STAGGER,
      easing: "ease-out",
      fill: "forwards",
    });

    container.appendChild(particle);
  }

  setTimeout(() => {
    document.body.removeChild(container);
  }, DURATION + count * STAGGER + 100);
};

const createSnowfall = (
  x: number,
  y: number,
  count: number,
  properties: { colors: string[] }
) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "0";
  container.style.width = "100%";
  container.style.height = "100%";
  container.style.pointerEvents = "none";
  container.style.zIndex = "9999";
  document.body.appendChild(container);

  for (let i = 0; i < count; i++) {
    const particle = document.createElement("div");
    particle.style.position = "absolute";
    particle.style.left = `${x}px`;
    particle.style.top = `${y}px`;
    const size = Math.random() * 6 + 4;
    particle.style.width = `${size}px`;
    particle.style.height = `${size}px`;
    particle.style.backgroundColor =
      properties.colors[Math.floor(Math.random() * properties.colors.length)];
    particle.style.borderRadius = "50%";
    particle.style.opacity = "1";

    requestAnimationFrame(() => {
      // 1. 短く上に上がる
      const initialUpwardY = -(Math.random() * 40 + 20);
      const initialSideways = Math.random() * 40 - 20;
      particle.style.transition =
        "transform 0.2s cubic-bezier(0, .7, .3, 1), opacity 0.5s ease-in";
      particle.style.transform = `translate(calc(-50% + ${initialSideways}px), calc(-50% + ${initialUpwardY}px))`;

      // 2. 少し遅れて、ゆっくり落ちる
      setTimeout(() => {
        const finalDownardY = Math.random() * 150 + 100;
        const finalSideways = Math.random() * 100 - 50;
        const rotation = Math.random() * 360;
        particle.style.transition =
          "transform 2s ease-in-out, opacity 2s ease-in";
        particle.style.transform = `translate(calc(-50% + ${finalSideways}px), calc(-50% + ${finalDownardY}px)) rotate(${rotation}deg) scale(0.5)`;
        particle.style.opacity = "0";
      }, 150);
    });

    container.appendChild(particle);
  }

  setTimeout(() => {
    document.body.removeChild(container);
  }, 2200);
};

export const availableEffects: Effect[] = [
  {
    name: "Cracker",
    icon: CelebrationSharpIcon,
    logic: (x, y) =>
      createBurst(x, y, 50, {
        colors: Object.values(PaletteColorHex),
        shapes: ["circle", "square", "triangle"],
      }),
  },
  {
    name: "Flower",
    icon: FilterVintageSharpIcon,
    logic: (x, y) =>
      createFlowerSpiral(x, y, 40, {
        colors: [
          PaletteColorHex.Orange,
          PaletteColorHex.Red,
          PaletteColorHex.Pink,
          PaletteColorHex.Lavender,
          PaletteColorHex.Purple,
        ],
      }),
  },
  {
    name: "Fireworks",
    icon: FlareSharpIcon,
    logic: (x, y) => {
      const combinations = [
        [PaletteColorHex.Blue, PaletteColorHex.Skyblue],
        [PaletteColorHex.Red, PaletteColorHex.Orange],
        [PaletteColorHex.Purple, PaletteColorHex.Lavender],
      ];
      createBurst(x, y, 70, {
        colors: combinations[Math.floor(Math.random() * combinations.length)],
        shapes: ["line"],
      });
    },
  },
  {
    name: "Snow",
    icon: AcUnitSharpIcon,
    logic: (x, y) =>
      createSnowfall(x, y, 50, {
        colors: [PaletteColorHex.Skyblue, "#FFFFFF"],
      }),
  },
  {
    name: "Star",
    icon: StarPurple500SharpIcon,
    logic: (x, y) =>
      createBurst(x, y, 10, {
        colors: [
          PaletteColorHex.Red,
          PaletteColorHex.Orange,
          PaletteColorHex.Pink,
        ],
        shapes: ["star"],
        sizeRange: { min: 16, max: 30 },
      }),
  },
  { name: "None", icon: MotionPhotosOffSharpIcon, logic: () => {} },
];

const effectsMap = new Map(availableEffects.map((e) => [e.name, e]));

export const triggerEffect = (
  effectName: EffectName,
  target: HTMLElement | { x: number; y: number }
) => {
  const effect = effectsMap.get(effectName);
  if (!effect) return;

  let x = 0,
    y = 0;
  if (target instanceof HTMLElement) {
    const rect = target.getBoundingClientRect();
    x = rect.left + rect.width / 2;
    y = rect.top + rect.height / 2;
  } else {
    x = target.x;
    y = target.y;
  }

  effect.logic(x, y);
};
